import React from "react";
import { Link } from "react-router-dom";
import { TreePine, Target, Waves, ArrowRight } from "lucide-react";

const CourseDetails = () => (
  <>
    {/* Course Overview */}
    <section id="overview" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
              Course Overview
            </span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
              A Course Worth Playing
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Willow Ridge Golf & Country Club features a beautifully
              maintained 18-hole course that challenges golfers of all skill
              levels. With lush fairways, strategic bunkers, and pristine
              greens set among mature trees, every round is a memorable
              experience.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              Our course offers stunning countryside views and a variety of
              hole designs that will test your accuracy and strategy. Whether
              you're a seasoned player or just starting out, Willow Ridge
              provides an exceptional golfing experience.
            </p>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-3">
                  <TreePine size={28} className="text-[#C9A96E]" />
                </div>
                <div className="text-2xl font-bold text-[#1B2A4A]">18</div>
                <div className="text-sm text-gray-500">Holes</div>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-3">
                  <Target size={28} className="text-[#C9A96E]" />
                </div>
                <div className="text-2xl font-bold text-[#1B2A4A]">72</div>
                <div className="text-sm text-gray-500">Par</div>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-3">
                  <Waves size={28} className="text-[#C9A96E]" />
                </div>
                <div className="text-2xl font-bold text-[#1B2A4A]">6,400</div>
                <div className="text-sm text-gray-500">Yards</div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-lg">
            <img
              src="/images/course1.jpg"
              alt="Willow Ridge Golf Course"
              className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Valley 9 */}
    <section id="valley-9" className="py-24 bg-[#f8f7f4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="/images/ssow6x7r_valley9.png"
                alt="Valley 9 Course"
                className="w-full h-[540px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="absolute -top-5 -right-5 w-28 h-28 rounded-full bg-white shadow-xl border-4 border-[#C9A96E] overflow-hidden z-10">
              <img
                src="/images/znffeoo4_valley9logo.png"
                alt="Valley 9 Logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
                New Addition
              </span>
              <span className="inline-flex items-center gap-1.5 bg-[#C9A96E] text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                Cart Accessible
              </span>
            </div>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
              Valley 9
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Introducing Valley 9 — our exciting new 9-hole par 3 course perfect for players of all skill levels. Whether you're warming up for a full round, working on your short game, or introducing someone new to the sport, Valley 9 offers a fun and accessible golfing experience.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              Set in a scenic valley on the property, this compact course features beautifully maintained greens and a variety of hole lengths that challenge your iron play and putting skills. A great option for a quick round when time is limited.
            </p>
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-[#1B2A4A]">9</div>
                <div className="text-sm text-gray-500">Holes</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-[#1B2A4A]">27</div>
                <div className="text-sm text-gray-500">Par</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-[#1B2A4A]">Par 3</div>
                <div className="text-sm text-gray-500">All Holes</div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm mb-8">
              <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wide">Valley 9 Rates</h4>
              <div className="space-y-2 text-sm">
                {[
                  { label: "9 Holes — Walking", price: "$20" },
                  { label: "9 Holes — Riding", price: "$30" },
                  { label: "Play 18 (extra 9)", price: "+$10" },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between text-gray-600">
                    <span>{row.label}</span>
                    <span className="font-semibold text-[#1B2A4A]">{row.price}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              * First come, first served. Large groups please call ahead to ensure availability.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Pro Shop */}
    <section id="pro-shop" className="py-24 bg-[#f8f7f4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
              2026 Season
            </span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
              Stocked Pro Shop
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              The Pro Shop offers the widest range of clothing and golf equipment. Get outfitted for the course with the latest Nike, Adidas, Levelwear, FootJoy and more top brands.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Don't forget to stop in and grab your balls, gloves, shoes, hats, and all kinds of golf accessories before teeing it up!
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              We also feature a selection of golf clubs and accessories for the course, including bags and products for all weather conditions.
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {["Nike", "Adidas", "Titleist", "FootJoy", "Under Armour", "TravisMathew", "Ralph Lauren", "Sunice", "Levelwear"].map((brand) => (
                <span key={brand} className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-semibold text-[#1B2A4A] shadow-sm">
                  {brand}
                </span>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#1B2A4A] text-white font-semibold rounded-md hover:bg-[#2a3d62] transition-colors duration-300"
            >
              Contact the Pro Shop <ArrowRight size={18} />
            </Link>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-lg">
            <img
              src="/images/070saj6y_Pro-Shop-Brand-File-pdf.jpg"
              alt="Pro Shop Brands — Nike, Titleist, Adidas, FootJoy and more"
              className="w-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    </section>
  </>
);

export { CourseDetails };
